import { NgClass, NgStyle } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    input,
    output,
    viewChild,
    viewChildren,
} from '@angular/core';
import { ArrayPipe, ButtonComponent } from '@interacta-shared/ui';
import {
    CarouselControl,
    CarouselStep,
    ICarouselConfig,
    ISlide,
} from '@modules/carousel/models/carousel/carousel.model';
import {
    carouselFocusRing,
    readableTextColor,
} from '@modules/carousel/utils/carousel-focus-ring.util';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'interacta-carousel-slide',
    templateUrl: './carousel-slide.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgStyle, NgClass, ButtonComponent, ArrayPipe, TranslatePipe],
})
export class CarouselSlideComponent {
    readonly slide = input.required<ISlide>();
    readonly index = input.required<number>();
    readonly totalItems = input.required<number>();
    readonly config = input.required<ICarouselConfig>();
    // The focus trap's tabbable lookup ignores `inert`: without region markers on
    // the active slide it wraps onto a control of a hidden slide, which cannot
    // take focus, and the next Tab leaves the dialog.
    readonly active = input(false);

    readonly nextStep = output<CarouselStep>();
    readonly closeCarousel = output<void>();

    protected readonly isLast = computed(
        () => this.index() === this.totalItems() - 1,
    );

    // Slide colours do not follow the app theme, so the ring colour comes from
    // the slide background instead of the theme's focus token.
    protected readonly focusRingColor = computed(() => {
        switch (carouselFocusRing(this.slide().colorBegin)) {
            case 'dark':
                return 'var(--in-color-nero-non-nero)';
            case 'light':
                return 'var(--in-color-bianco)';
            default:
                return 'var(--in-theme-border-focus)';
        }
    });

    protected readonly skipColor = computed(() =>
        readableTextColor(
            this.config().colorNameSkipBtn,
            this.slide().colorBegin,
        ),
    );
    protected readonly nextColor = computed(() =>
        readableTextColor(
            this.config().colorNameNextBtn,
            this.slide().colorBegin,
        ),
    );

    private readonly dots = viewChildren<ElementRef<HTMLButtonElement>>('dot');
    private readonly nextButton = viewChild<
        string,
        ElementRef<HTMLButtonElement>
    >('nextButton', { read: ElementRef });
    private readonly doneButton = viewChild<
        string,
        ElementRef<HTMLButtonElement>
    >('doneButton', { read: ElementRef });

    focusControl(control: CarouselControl): void {
        const target =
            control === 'dot'
                ? this.dots()[this.index()]
                : (this.nextButton() ?? this.doneButton());
        target?.nativeElement.focus({ preventScroll: true });
    }
}
